/** @jsx isolateComponent */
import { css } from '@emotion/react';

import type { IconName } from '@zapier/design-system';
import { Colors, Icon, Link, Typography } from '@zapier/design-system';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { useUniversalNavContext } from '../../../hooks/useUniversalNavContext';
import { emitUserInteractionEvent } from '../../../utils/avroEvents';
import { ArrowLinkIcon } from '../ArrowLinkIcon';
import type { NavItemVariantA } from '../../../constants/links';

type VariantAMegaMenuRightProps = {
  header: NavItemVariantA['promoArea']['header'];
  submenu: NavItemVariantA['promoArea']['submenu'];
  /**
   * Function called when closing menu on a link from within the MegaMenu
   */
  onCloseMenu?: () => void;
};

const Styles = labelStyles('StaticProductRight', {
  root: css({
    display: 'flex',
    flexDirection: 'column',
    // necessary to prevent the right side of focus outlines from being hidden
    marginRight: 1,
    width: 250,
  }),
  header: css({
    display: 'flex',
    flexDirection: 'column',
    textDecoration: 'none',
  }),
  linkHeader: css({
    '&:hover [data-tag="heading"]': {
      color: Colors.zapier,
      textDecoration: 'none',
    },
    '&:hover [data-tag="subheading"]': {
      textDecoration: 'underline',
    },
    '&:hover [data-tag="arrow-icon-wrapper"]': {
      marginLeft: 4,
      opacity: 1,
    },
    '&:focus': {
      outline: `1px solid ${Colors.blueJeans}`,
    },
  }),
  heading: css({
    ...Typography.pageHeader9,
    color: Colors.earth,
    lineHeight: '24px',
  }),
  subheading: css({
    ...Typography.smallPrint1,
    color: Colors.neutral700,
    display: 'block',
    marginTop: 8,
  }),
  divider: css({
    margin: '16px 0',
    height: 1,
    backgroundColor: Colors.neutral300,
  }),
  linkContainer: css({
    display: 'flex',
    columnGap: 10,
    marginBottom: 8,
    textDecoration: 'none',
    color: Colors.earth,
    '&:hover [data-tag="link-heading"]': {
      color: Colors.zapier,
      textDecoration: 'none',
    },
    '&:hover [data-tag="link-icon"]': {
      color: Colors.zapier,
    },
    '&:hover [data-tag="tag-label"]': {
      textDecoration: 'none',
    },
    '&:hover [data-tag="link-description"]': {
      textDecoration: 'underline',
    },
    '&:hover [data-tag="arrow-icon-wrapper"]': {
      marginLeft: 4,
      opacity: 1,
      color: Colors.neutral700,
    },
    '&:focus': {
      outline: `1px solid ${Colors.blue}`,
    },
  }),
  linkIcon: css({
    marginTop: 6,
  }),
  linkTextContainer: css({
    display: 'flex',
    flexDirection: 'column',
  }),
  linkHeading: css({
    ...Typography.paragraph3Semibold,
    marginRight: 6,
    lineHeight: '30px',
  }),
  linkHeadingRow: css({
    display: 'flex',
    alignItems: 'center',
  }),
  descriptionWrapper: css({
    ...Typography.smallPrint3,
    lineHeight: '20px',
  }),
  linkDescription: css({
    ...Typography.smallPrint3,
    color: Colors.neutral700,
    lineHeight: '20px',
  }),
  tagLabel: css({
    // These are requests from Talia,
    // even though they do not match Figma, FYI
    ...Typography.base,
    fontSize: '10px',
    lineHeight: '14px',
    fontWeight: 600,
    padding: '2px 6px',
    borderRadius: 100,
    backgroundColor: Colors.lavender,
    textDecoration: 'none',
    textTransform: 'lowercase',
  }),
});

/**
 * @private
 * Wrapper for the content inside the Product tab's right side
 */
const VariantAProductRight = ({
  header,
  submenu,
  onCloseMenu,
}: VariantAMegaMenuRightProps) => {
  const { getFullyQualifiedURL } = useUniversalNavContext();

  const handleEvent = (text: string, eventType: string) => {
    emitUserInteractionEvent({
      event_action: eventType,
      option_selected: `Product tab Right: ${text}`,
    });

    if (eventType === 'click' && onCloseMenu) onCloseMenu();
  };

  const Header = () => {
    if (header.href) {
      return (
        <Link
          color={null}
          css={[Styles.header, Styles.linkHeader]}
          href={getFullyQualifiedURL(header.href)}
          target={header.target}
          onClick={() => handleEvent('Early Access Program heading', 'click')}
          onMouseEnter={() =>
            handleEvent('Early Access Program heading', 'hover')
          }
        >
          <span css={Styles.heading} data-tag="heading">
            {header.heading}
          </span>
          <div>
            <span css={Styles.subheading} data-tag="subheading">
              {header.subheading}
              <ArrowLinkIcon />
            </span>
          </div>
        </Link>
      );
    } else {
      return (
        <header css={Styles.header}>
          <span css={Styles.heading} data-tag="heading">
            {header.heading}
          </span>
          <div>
            <span css={Styles.subheading} data-tag="subheading">
              {header.subheading}
            </span>
          </div>
        </header>
      );
    }
  };

  return (
    <div css={Styles.root}>
      <Header />

      <hr css={Styles.divider} />

      <ul>
        {submenu.map((link) => (
          <li key={link.text}>
            <Link
              color={null}
              css={Styles.linkContainer}
              href={getFullyQualifiedURL(link.href)}
              target={link.target}
              onClick={() => handleEvent(link.text, 'click')}
              onMouseEnter={() => handleEvent(link.text, 'hover')}
            >
              <span css={Styles.linkIcon} data-tag="link-icon">
                <Icon name={link.icon as IconName} size={15} />
              </span>
              <div css={Styles.linkTextContainer}>
                <div css={Styles.linkHeadingRow}>
                  <span css={Styles.linkHeading} data-tag="link-heading">
                    {link.text}
                  </span>
                  {link.tag && (
                    <span css={Styles.tagLabel} data-tag="tag-label">
                      {link.tag}
                    </span>
                  )}
                </div>
                <div css={Styles.descriptionWrapper}>
                  <span
                    css={Styles.linkDescription}
                    data-tag="link-description"
                  >
                    {link.description}
                  </span>
                  <ArrowLinkIcon />
                </div>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default VariantAProductRight;
